import { useState } from 'react';
import { Trash2, Edit, CheckCircle, MoreVertical, Pause, Play, ChevronDown, ChevronUp, Receipt, Plus, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Debt, useDebts } from '@/hooks/useDebts';
import { useTransactions } from '@/hooks/useTransactions';
import { cn } from '@/lib/utils';

interface DebtCardProps {
  debt: Debt;
  order?: number;
  isTarget?: boolean;
  onMarkPaid: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdate: (debt: Partial<Debt> & { id: string }) => void;
  currency?: string;
}

export function DebtCard({
  debt,
  order,
  isTarget,
  onMarkPaid,
  onDelete,
  onUpdate,
  currency = 'CRC',
}: DebtCardProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [quickPaymentOpen, setQuickPaymentOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({
    name: debt.name,
    creditor: debt.creditor || '',
    initial_balance: debt.initial_balance,
    current_balance: debt.current_balance,
    interest_rate: debt.interest_rate,
    minimum_payment: debt.minimum_payment,
    due_day: debt.due_day || '',
  });
  const { useDebtPayments } = useDebts();
  const { data: payments = [] } = useDebtPayments(debt.id);
  const { createTransaction } = useTransactions();

  const progress = debt.initial_balance > 0
    ? ((debt.initial_balance - debt.current_balance) / debt.initial_balance) * 100
    : 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-CR', {
      day: 'numeric',
      month: 'short',
    });
  };

  const handleEditSave = () => {
    onUpdate({
      id: debt.id,
      name: editData.name,
      creditor: editData.creditor || null,
      initial_balance: Number(editData.initial_balance),
      current_balance: Number(editData.current_balance),
      interest_rate: Number(editData.interest_rate),
      minimum_payment: Number(editData.minimum_payment),
      due_day: editData.due_day ? Number(editData.due_day) : null,
    });
    setEditOpen(false);
  };

  const openEditDialog = () => {
    setEditData({
      name: debt.name,
      creditor: debt.creditor || '',
      initial_balance: debt.initial_balance,
      current_balance: debt.current_balance,
      interest_rate: debt.interest_rate,
      minimum_payment: debt.minimum_payment,
      due_day: debt.due_day || '',
    });
    setEditOpen(true);
  };

  const handleQuickPayment = () => {
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) return;

    createTransaction.mutate({
      date: new Date().toISOString().split('T')[0],
      type: 'debt_payment',
      amount,
      category: 'debt_payment',
      payment_method: 'transfer',
      currency: currency as 'USD' | 'CRC',
      notes: `Pago a ${debt.name}`,
      debt_id: debt.id,
    }, {
      onSuccess: () => {
        setQuickPaymentOpen(false);
        setPaymentAmount('');
      }
    });
  };

  const statusColors = {
    active: 'bg-primary/10 text-primary',
    paid_off: 'bg-green-500/10 text-green-600',
    paused: 'bg-muted text-muted-foreground',
  };

  const statusLabels = {
    active: 'Activa',
    paid_off: 'Pagada',
    paused: 'Pausada',
  };

  return (
    <>
      <Card className={cn(
        'relative transition-all duration-200',
        isTarget && 'ring-2 ring-primary shadow-lg',
        debt.status === 'paid_off' && 'opacity-75'
      )}>
        {isTarget && (
          <div className="absolute -top-3 left-4 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-full font-medium">
            🎯 Próxima a pagar
          </div>
        )}
        {order && debt.status === 'active' && (
          <div className="absolute -top-2 -right-2 bg-secondary text-secondary-foreground w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold">
            {order}
          </div>
        )}
        
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-lg">{debt.name}</h3>
              {debt.creditor && (
                <p className="text-sm text-muted-foreground">{debt.creditor}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={statusColors[debt.status]}>
                {statusLabels[debt.status]}
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={openEditDialog}>
                    <Edit className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                  {debt.status === 'active' && (
                    <>
                      <DropdownMenuItem onClick={() => setQuickPaymentOpen(true)}>
                        <DollarSign className="mr-2 h-4 w-4 text-green-500" />
                        Registrar pago
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setPaidDialogOpen(true)}>
                        <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                        Marcar como pagada
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => onUpdate({ id: debt.id, status: 'paused' })}
                      >
                        <Pause className="mr-2 h-4 w-4" />
                        Pausar
                      </DropdownMenuItem>
                    </>
                  )}
                  {debt.status === 'paused' && (
                    <DropdownMenuItem 
                      onClick={() => onUpdate({ id: debt.id, status: 'active' })}
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Reactivar
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Saldo actual</p>
              <p className="font-bold text-xl text-foreground">
                {formatCurrency(debt.current_balance)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Pago mínimo</p>
              <p className="font-semibold text-foreground">
                {formatCurrency(debt.minimum_payment)}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progreso</span>
              <span className="font-medium">{progress.toFixed(1)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Pagado: {formatCurrency(debt.initial_balance - debt.current_balance)}</span>
              <span>Total: {formatCurrency(debt.initial_balance)}</span>
            </div>
          </div>

          {debt.interest_rate > 0 && (
            <div className="flex justify-between text-sm pt-2 border-t">
              <span className="text-muted-foreground">Tasa de interés</span>
              <span className="font-medium">{debt.interest_rate}% anual</span>
            </div>
          )}

          {/* Payment History */}
          {payments.length > 0 && (
            <Collapsible open={paymentsOpen} onOpenChange={setPaymentsOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full justify-between p-0 h-auto pt-2 border-t">
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Receipt className="h-4 w-4" />
                    Historial de pagos ({payments.length})
                  </span>
                  {paymentsOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 space-y-2">
                {payments.map((payment) => (
                  <div 
                    key={payment.id} 
                    className="flex justify-between items-center text-sm bg-muted/50 rounded-lg px-3 py-2"
                  >
                    <span className="text-muted-foreground">{formatDate(payment.date)}</span>
                    <span className="font-medium text-green-600">
                      -{formatCurrency(payment.amount)}
                    </span>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Quick Payment Button */}
          {debt.status === 'active' && (
            <Button 
              onClick={() => setQuickPaymentOpen(true)}
              className="w-full gap-2"
              variant="outline"
            >
              <Plus className="h-4 w-4" />
              Registrar pago
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta deuda?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente "{debt.name}" de tu lista de deudas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onDelete(debt.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark as Paid Confirmation */}
      <AlertDialog open={paidDialogOpen} onOpenChange={setPaidDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>🎉 ¡Felicidades!</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Confirmas que has liquidado completamente "{debt.name}"? Esto marcará la deuda como pagada y liberará el pago mínimo para la siguiente deuda.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => onMarkPaid(debt.id)}>
              ¡Sí, está pagada!
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Payment Dialog */}
      <Dialog open={quickPaymentOpen} onOpenChange={setQuickPaymentOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Registrar pago</DialogTitle>
            <DialogDescription>
              Registra un pago para "{debt.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="quick-amount">Monto del pago</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {currency === 'CRC' ? '₡' : '$'}
                </span>
                <Input
                  id="quick-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="pl-8"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPaymentAmount(debt.minimum_payment.toString())}
              >
                Pago mínimo ({formatCurrency(debt.minimum_payment)})
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPaymentAmount(debt.current_balance.toString())}
              >
                Saldo total
              </Button>
            </div>
            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <p><strong>Saldo actual:</strong> {formatCurrency(debt.current_balance)}</p>
              {paymentAmount && parseFloat(paymentAmount) > 0 && (
                <p className="text-green-600 mt-1">
                  <strong>Nuevo saldo:</strong> {formatCurrency(Math.max(0, debt.current_balance - parseFloat(paymentAmount)))}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickPaymentOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleQuickPayment}
              disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || createTransaction.isPending}
            >
              {createTransaction.isPending ? 'Guardando...' : 'Registrar pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Debt Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Editar Deuda</DialogTitle>
            <DialogDescription>
              Modifica los datos de "{debt.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nombre *</Label>
                <Input
                  id="edit-name"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-creditor">Acreedor</Label>
                <Input
                  id="edit-creditor"
                  value={editData.creditor}
                  onChange={(e) => setEditData({ ...editData, creditor: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-initial">Saldo inicial</Label>
                <Input
                  id="edit-initial"
                  type="number"
                  step="0.01"
                  value={editData.initial_balance}
                  onChange={(e) => setEditData({ ...editData, initial_balance: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-current">Saldo actual</Label>
                <Input
                  id="edit-current"
                  type="number"
                  step="0.01"
                  value={editData.current_balance}
                  onChange={(e) => setEditData({ ...editData, current_balance: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-rate">Tasa de interés (%)</Label>
                <Input
                  id="edit-rate"
                  type="number"
                  step="0.01"
                  value={editData.interest_rate}
                  onChange={(e) => setEditData({ ...editData, interest_rate: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-payment">Pago mínimo</Label>
                <Input
                  id="edit-payment"
                  type="number"
                  step="0.01"
                  value={editData.minimum_payment}
                  onChange={(e) => setEditData({ ...editData, minimum_payment: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-due">Día de pago (1-31)</Label>
              <Input
                id="edit-due"
                type="number"
                min="1"
                max="31"
                value={editData.due_day}
                onChange={(e) => setEditData({ ...editData, due_day: e.target.value ? parseInt(e.target.value) : '' })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleEditSave}
              disabled={!editData.name || editData.minimum_payment <= 0}
            >
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
